import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import outletService from "../api/services/outletService";
import { handleApiError } from "../utils/apiHelpers";

export const suspendOutletService = createAsyncThunk(
  "service/suspendOutlet",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.suspendOutlet(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const enableOutletService = createAsyncThunk(
  "service/enableOutlet",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.enableOutlet(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteOutletService = createAsyncThunk(
  "service/deleteOutlet",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.deleteOutlet(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const acceptOutletService = createAsyncThunk(
  "service/acceptOutlet",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.acceptOnboardingOutlet(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOutletWeeklyPayments = createAsyncThunk(
  "service/weeklyPayments",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.fetchWeeklyPayments(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelTripService = createAsyncThunk(
  "service/cancelTrip",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.cancelOrder(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const driverSearchService = createAsyncThunk(
  "service/driverSearch",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.driverSearch(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const assignDriverService = createAsyncThunk(
  "service/assignDriver",
  async (data, thunkAPI) => {
    try {
      const response = await outletService.assignDriver(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const outletSlice = createSlice({
  name: "outlets",
  initialState: {
    outletDetailData: {},
    selectedOutletOrder: [],
    onboardingOutlets: {},
    weeklyPayments: {},
    cancelTripData: {},
    loading: false,
    error: null,
  },

  reducers: {
    updateOutletData: (state, action) => {
      state.outletDetailData = action.payload;
    },
    selectOutletOrders: (state, action) => {
      state.selectedOutletOrder = action.payload;
    },
    updateOnboardingOutlets: (state, action) => {
      state.onboardingOutlets = action.payload;
    },
    updateCancelTripData: (state, action) => {
      state.cancelTripData = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(suspendOutletService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(suspendOutletService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(suspendOutletService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(enableOutletService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enableOutletService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(enableOutletService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteOutletService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOutletService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(deleteOutletService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(acceptOutletService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptOutletService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(acceptOutletService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchOutletWeeklyPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOutletWeeklyPayments.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(fetchOutletWeeklyPayments.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(cancelTripService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelTripService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(cancelTripService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(driverSearchService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(driverSearchService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(driverSearchService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(assignDriverService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignDriverService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(assignDriverService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateOutletData,
  selectOutletOrders,
  updateOnboardingOutlets,
  updateCancelTripData,
} = outletSlice.actions;

export default outletSlice.reducer;
